import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, CheckIcon, XMarkIcon, PhoneIcon, TrashIcon, ClockIcon, ChatBubbleLeftRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import type { Lead } from './leadsTypes';
import type { Run } from './leadsTypes';
import { TemperaturePill, DispositionBadge } from '../../components/intelligence';

type LeadsMobileCardsProps = {
  leads: Lead[];
  runs: Map<string, Run>;
  editingId: string | null;
  editData: Partial<Lead>;
  setEditData: (d: Partial<Lead>) => void;
  getRunName: (runId: string | null, created_at?: string, source?: string | null) => string;
  formatDateDDMMYYYY: (iso: string) => string;
  onEdit: (lead: Lead) => void;
  onSave: (leadId: string) => void;
  onCancel: () => void;
  onRunCampaignForLead: (lead: Lead) => void;
  onOpenDeleteConfirm: (lead: Lead) => void;
  onSendSms?: (lead: Lead) => void;
  onSendEmail?: (lead: Lead) => void;
  canSendEmail?: boolean;
  onViewHistory: (lead: Lead) => void;
  selectedLeadIds: Set<string>;
  isAllOnPageSelected: boolean;
  onToggleSelection: (lead: Lead) => void;
  onSelectAllOnPage: () => void;
};

const LeadsMobileCards: React.FC<LeadsMobileCardsProps> = ({
  leads,
  getRunName,
  formatDateDDMMYYYY,
  editingId,
  editData,
  setEditData,
  onEdit,
  onSave,
  onCancel,
  onRunCampaignForLead,
  onOpenDeleteConfirm,
  onSendSms,
  onSendEmail,
  canSendEmail = true,
  onViewHistory,
  selectedLeadIds,
  isAllOnPageSelected,
  onToggleSelection,
  onSelectAllOnPage,
}) => {
  const navigate = useNavigate();
  return (
  <div className="lg:hidden">
    {leads.length > 0 && (
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10">
        <label className="cursor-pointer inline-flex items-center justify-center relative w-5 h-5 rounded-md focus-within:ring-2 focus-within:ring-cyan-500/50 focus-within:ring-offset-2 focus-within:ring-offset-slate-900 flex-shrink-0">
          <input
            type="checkbox"
            checked={isAllOnPageSelected}
            onChange={onSelectAllOnPage}
            className="peer sr-only"
          />
          <span className="absolute inset-0 w-5 h-5 rounded-md border-2 border-slate-500 bg-slate-800/80 transition-all duration-200 peer-checked:bg-cyan-500 peer-checked:border-cyan-400 hover:border-slate-400" />
          <CheckIcon className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none relative z-10" />
        </label>
        <span className="text-xs text-gray-400">
          {isAllOnPageSelected ? 'Deselect all' : 'Select all on page'}
        </span>
      </div>
    )}
    <div className="divide-y divide-white/5">
      {leads.map((lead) => {
        const isEditing = editingId === lead.id;
        const isSelected = selectedLeadIds.has(lead.id);
        return (
          <div key={lead.id} className={`p-4 hover:bg-white/5 transition-colors ${isSelected ? 'bg-cyan-500/10' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <label className="cursor-pointer inline-flex items-center justify-center relative w-5 h-5 rounded-md mt-0.5 flex-shrink-0 focus-within:ring-2 focus-within:ring-cyan-500/50 focus-within:ring-offset-2 focus-within:ring-offset-slate-900">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelection(lead)}
                    className="peer sr-only"
                    title={isSelected ? 'Deselect' : 'Select for campaign'}
                  />
                  <span className="absolute inset-0 w-5 h-5 rounded-md border-2 border-slate-500 bg-slate-800/80 transition-all duration-200 peer-checked:bg-cyan-500 peer-checked:border-cyan-400 hover:border-slate-400" />
                  <CheckIcon className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none relative z-10" />
                </label>
                <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                    <h3 className="text-sm font-semibold truncate">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.name || ''}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                          placeholder="Name"
                        />
                      ) : (
                        <button type="button" onClick={() => navigate(`/leads/${lead.id}`)} className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors text-left">
                          {lead.name}
                        </button>
                      )}
                    </h3>
                    {!isEditing && <TemperaturePill temperature={lead.current_temperature} />}
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => navigate(`/leads/${lead.id}`)} className="p-1 text-slate-400 hover:text-cyan-300 transition-colors" title="View lead details">
                        <ClockIcon className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => onEdit(lead)} className="p-1 text-cyan-400 hover:text-cyan-300 transition-colors" title="Edit">
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => onRunCampaignForLead(lead)} className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors" title="Run campaign">
                        <PhoneIcon className="w-4 h-4" />
                      </button>
                      {onSendSms && (
                        <button type="button" onClick={() => onSendSms(lead)} className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors" title="Send SMS">
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        </button>
                      )}
                      {onSendEmail && (lead.email || '').trim() && (
                        <button
                          type="button"
                          onClick={() => onSendEmail(lead)}
                          disabled={!canSendEmail}
                          className={`p-1 transition-colors ${canSendEmail ? 'text-sky-400 hover:text-sky-300' : 'text-sky-400/35 cursor-not-allowed'}`}
                          title={
                            canSendEmail
                              ? 'Send email'
                              : 'Email sending is not available on your plan'
                          }
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button type="button" onClick={() => onOpenDeleteConfirm(lead)} className="p-1 text-red-400 hover:text-red-300 transition-colors" title="Delete lead">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {!isEditing && lead.current_disposition && (
                  <div className="mb-2">
                    <DispositionBadge disposition={lead.current_disposition} />
                  </div>
                )}
                <div className="space-y-1.5 text-xs text-gray-300">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-400 w-16 flex-shrink-0">Phone:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.phone || ''}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                        placeholder="Phone"
                      />
                    ) : (
                      <span className="truncate">{lead.phone}</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-400 w-16 flex-shrink-0">Email:</span>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editData.email || ''}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                        placeholder="Email"
                      />
                    ) : (
                      <span className="truncate">{lead.email || '-'}</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-400 w-16 flex-shrink-0">Source:</span>
                    <span className="truncate flex-1" title={getRunName(lead.run_id, lead.created_at, lead.source)}>
                      {getRunName(lead.run_id, lead.created_at, lead.source)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-400 w-16 flex-shrink-0">Attempts:</span>
                      <span>{lead.total_attempts ?? lead.retry_count}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-gray-400 mr-2">Created:</span>
                      <span>{formatDateDDMMYYYY(lead.created_at)}</span>
                    </div>
                  </div>
                  {lead.last_contact_at && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-400 w-16 flex-shrink-0">Last call:</span>
                      <span>{formatDateDDMMYYYY(lead.last_contact_at)}</span>
                    </div>
                  )}
                </div>
                </div>
              </div>
              {isEditing && (
                <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                  <button type="button" onClick={() => onSave(lead.id)} className="p-1.5 text-green-400 hover:text-green-300 transition-colors" title="Save">
                    <CheckIcon className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={onCancel} className="p-1.5 text-red-400 hover:text-red-300 transition-colors" title="Cancel">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
  );
};

export default LeadsMobileCards;
